// Rename Folder Dialog Component
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Folder } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import Button from '@/components/Button';
import Input from '@/components/Input';

interface RenameFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: { id: number; name: string; color?: string } | null;
}

export default function RenameFolderDialog({ open, onOpenChange, folder }: RenameFolderDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const queryClient = useQueryClient();

  // Update name when folder changes
  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setError('');
    }
  }, [folder]);

  // Update folder mutation
  const updateFolderMutation = useMutation({
    mutationFn: (data: { name: string }) => api.updateFolder(folder!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['feeds'] });
      setError('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to rename folder');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a folder name');
      return;
    }

    if (name.length > 100) {
      setError('Folder name must be 100 characters or less');
      return;
    }

    if (name.trim() === folder?.name) {
      onOpenChange(false);
      return;
    }

    updateFolderMutation.mutate({ name: name.trim() });
  };

  if (!folder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Folder className="h-5 w-5 text-primary" />
            <DialogTitle>Rename Folder</DialogTitle>
          </div>
          <DialogDescription>
            Change the name of this folder
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="folder-name" className="text-sm font-medium">
              Folder Name
            </label>
            <Input
              id="folder-name"
              type="text"
              placeholder="Folder name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={updateFolderMutation.isPending}
              maxLength={100}
              autoFocus
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateFolderMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateFolderMutation.isPending}>
              {updateFolderMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Renaming...
                </>
              ) : (
                'Rename'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}




